import { FC, useState } from "react";
import { ToolCallInfo } from "../../store/ChatAction";
import "./ToolCallDisplay.scss";

export interface ToolCallDisplayProps {
    toolCalls: ToolCallInfo[];
    /** When true, shows technical details (tool name, input/output). Default shows user-friendly labels only. */
    debugMode?: boolean;
}

interface ToolCallItemProps {
    toolCall: ToolCallInfo;
    debugMode?: boolean;
}

const CopyButton: FC<{ text: string }> = ({ text }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async (e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            // Fallback for older browsers
            const textarea = document.createElement("textarea");
            textarea.value = text;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand("copy");
            document.body.removeChild(textarea);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        }
    };

    return (
        <button
            className="tool-call-item__copy-btn"
            onClick={handleCopy}
            title="Copy to clipboard"
        >
            {copied ? "✓" : "⧉"}
        </button>
    );
};

/**
 * Gets the display text for a tool call, using fallback chain:
 * label -> displayName -> toolName -> "Working..."
 */
const getDisplayLabel = (toolCall: ToolCallInfo): string => {
    return toolCall.label ?? toolCall.displayName ?? toolCall.name ?? "Working...";
};

/**
 * User-friendly tool progress item (non-debug mode)
 * Shows just the label with a spinner or checkmark
 */
const ToolProgressItem: FC<ToolCallItemProps> = ({ toolCall }) => {
    const label = getDisplayLabel(toolCall);

    return (
        <div className={`tool-progress-item ${toolCall.isExecuting ? "tool-progress-item--executing" : ""} ${toolCall.error ? "tool-progress-item--error" : ""}`}>
            <span className="tool-progress-item__icon">
                {toolCall.isExecuting ? (
                    <span className="tool-progress-item__spinner" aria-label="executing" />
                ) : toolCall.error ? (
                    <span className="tool-progress-item__status tool-progress-item__status--error">!</span>
                ) : (
                    <span className="tool-progress-item__status tool-progress-item__status--success">&#10003;</span>
                )}
            </span>
            <span className="tool-progress-item__label">{label}</span>
        </div>
    );
};

/**
 * Debug mode tool call item with expandable details
 * Shows label, tool name, and expandable input/output
 */
const ToolCallItem: FC<ToolCallItemProps> = ({ toolCall }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const toggleExpanded = () => setIsExpanded(!isExpanded);

    const formatJson = (data: unknown): string => {
        if (data === undefined || data === null) return "";
        try {
            // If it's a string, try to parse it as JSON first (server may send stringified JSON)
            if (typeof data === "string") {
                try {
                    const parsed = JSON.parse(data);
                    return JSON.stringify(parsed, null, 2);
                } catch {
                    // Not valid JSON, return as-is
                    return data;
                }
            }
            return JSON.stringify(data, null, 2);
        } catch {
            return String(data);
        }
    };

    const label = getDisplayLabel(toolCall);
    const duration = toolCall.endTime
        ? `${((toolCall.endTime - toolCall.startTime) / 1000).toFixed(2)}s`
        : null;

    const hasInput = toolCall.input !== undefined && toolCall.input !== null;
    const hasResult = toolCall.result !== undefined && toolCall.result !== null;
    const hasError = !!toolCall.error;

    return (
        <div className={`tool-call-item ${toolCall.isExecuting ? "tool-call-item--executing" : ""}`}>
            <button
                className="tool-call-item__header"
                onClick={toggleExpanded}
                aria-expanded={isExpanded}
            >
                <span className="tool-call-item__icon">
                    {toolCall.isExecuting ? (
                        <span className="tool-call-item__spinner" aria-label="executing" />
                    ) : toolCall.error ? (
                        <span className="tool-call-item__status tool-call-item__status--error">!</span>
                    ) : (
                        <span className="tool-call-item__status tool-call-item__status--success">&#10003;</span>
                    )}
                </span>
                <span className="tool-call-item__info">
                    <span className="tool-call-item__label">{label}</span>
                    <span className="tool-call-item__name">{toolCall.name}</span>
                </span>
                {duration && <span className="tool-call-item__duration">{duration}</span>}
                <span className={`tool-call-item__chevron ${isExpanded ? "tool-call-item__chevron--expanded" : ""}`}>
                    &#9662;
                </span>
            </button>

            {isExpanded && (
                <div className="tool-call-item__content">
                    {hasInput && (
                        <div className="tool-call-item__section">
                            <div className="tool-call-item__section-header">
                                <span className="tool-call-item__section-label">Input</span>
                                <CopyButton text={formatJson(toolCall.input)} />
                            </div>
                            <pre className="tool-call-item__code">{formatJson(toolCall.input)}</pre>
                        </div>
                    )}
                    {hasResult && (
                        <div className="tool-call-item__section">
                            <div className="tool-call-item__section-header">
                                <span className="tool-call-item__section-label">Output</span>
                                <CopyButton text={formatJson(toolCall.result)} />
                            </div>
                            <pre className="tool-call-item__code">{formatJson(toolCall.result)}</pre>
                        </div>
                    )}
                    {hasError && (
                        <div className="tool-call-item__section">
                            <div className="tool-call-item__section-header">
                                <span className="tool-call-item__section-label tool-call-item__section-label--error">Error</span>
                                <CopyButton text={toolCall.error || ""} />
                            </div>
                            <pre className="tool-call-item__code tool-call-item__code--error">{toolCall.error}</pre>
                        </div>
                    )}
                    {!hasInput && !hasResult && !hasError && (
                        <div className="tool-call-item__empty">No data available</div>
                    )}
                </div>
            )}
        </div>
    );
};

export const ToolCallDisplay: FC<ToolCallDisplayProps> = ({ toolCalls, debugMode = false }) => {
    if (!toolCalls || toolCalls.length === 0) {
        return null;
    }

    // Debug mode: show all tools with full expandable details
    if (debugMode) {
        return (
            <div className="tool-call-display">
                <div className="tool-call-display__header">
                    <span className="tool-call-display__icon">&#9881;</span>
                    <span className="tool-call-display__title">Tool Calls ({toolCalls.length})</span>
                </div>
                <div className="tool-call-display__list">
                    {toolCalls.map((toolCall, index) => (
                        <ToolCallItem key={`${toolCall.name}-${toolCall.startTime}-${index}`} toolCall={toolCall} />
                    ))}
                </div>
            </div>
        );
    }

    // Normal mode: filter out hidden tools and show user-friendly progress
    const visibleToolCalls = toolCalls.filter((tc) => !tc.hidden);

    if (visibleToolCalls.length === 0) {
        return null;
    }

    return (
        <div className="tool-progress-display">
            {visibleToolCalls.map((toolCall, index) => (
                <ToolProgressItem key={`${toolCall.name}-${toolCall.startTime}-${index}`} toolCall={toolCall} />
            ))}
        </div>
    );
};
